import { action, computed, makeObservable, observable } from "mobx";
import type { UploadSource } from "../../network/domain/UploadSource";
import type { ICodeAndSourceValidationFetcher } from "../../../network/fetchers/CodeAndSourceValidationFetcher";
import type { ICurrentUploadSettingsStore } from "../../CurrentUploadSettingsStore";

export interface IUploadCodeAndSourceSelectionStore {
    readonly selectedSourceForUpload: UploadSource | null;
    readonly sourcesAvailableForUpload: UploadSource[];
    readonly uploadCodeAndSourceValid: boolean | null;
    uploadCode: string | null;
    uploadCodeLength: number;
    uploadCodeLengthOk: boolean;
    ableToValidateCodeAndSource: boolean;
    validating: boolean;
    uploadCodeError: boolean;
    sourceSelectionError: boolean;
    expiredUploadCode: boolean;
    setSelectedSource(source: UploadSource | null): void;
    setUploadCode(uploadCode: string): void;
    validateCodeAndSource(): Promise<void>;
}

export class UploadCodeAndSourceSelectionStore implements IUploadCodeAndSourceSelectionStore {
    public static readonly Pow4UploadAsUserUrl = "/POW4/UploadAsUser";
    public static readonly Pow4UploadUrl = "/POW4/Upload";
    
    @observable
    private _selectedSourceForUpload: UploadSource | null = null;

    @observable
    public uploadCode: string | null;

    @observable
    private _uploadCodeAndSourceValid: boolean | null = null;

    @observable
    public validating = false;

    @observable
    public uploadCodeError = false;

    @observable
    public sourceSelectionError = false;

    @observable
    public expiredUploadCode = false;

    public readonly uploadCodeLength: number = 6;

    constructor(
        private readonly codeAndSourceValidationFetcher: ICodeAndSourceValidationFetcher,
        private readonly currentUploadSettingsStore: ICurrentUploadSettingsStore,
    ) {
        makeObservable(this);
        
        this.uploadCode = this.currentUploadSettingsStore.uploadCode?.toUpperCase() ?? null;
    }
    
    get baseUrl(): string {
        return this.currentUploadSettingsStore.userUploadsWithoutCode
            ? UploadCodeAndSourceSelectionStore.Pow4UploadAsUserUrl
            : UploadCodeAndSourceSelectionStore.Pow4UploadUrl;
    }

    @computed
    get sourcesAvailableForUpload(): UploadSource[] {
        return this.currentUploadSettingsStore.sourcesAvailableForUpload.toSorted(this.sortFunction);
    }

    @computed
    get uploadCodeAndSourceValid(): boolean | null {
        if (this.currentUploadSettingsStore.userUploadsWithoutCode) return true;
        return this._uploadCodeAndSourceValid;
    }

    @computed
    get selectedSourceForUpload(): UploadSource | null {
        if (this._selectedSourceForUpload)
            return this._selectedSourceForUpload;
        
        if (this.currentUploadSettingsStore.preferredSourceForUpload) {
            const result = this.sourcesAvailableForUpload.find(s => s.id === this.currentUploadSettingsStore.preferredSourceForUpload?.id) ?? null;
            if (result)
                return result;
        }

        //No preferred source, but user is authenticated, so just make first source selected,
        // it will still be possible to change it as a user from the dropdown in the header
        if (this.currentUploadSettingsStore.userUploadsWithoutCode) {
            return this.sourcesAvailableForUpload[0];
        }
        
        return null;
    }

    @action
    setSelectedSource(source: UploadSource | null) {
        //Prevent changing of source when combination of generated code and source has been validated and acting as a patient
        if (this._uploadCodeAndSourceValid && !this.currentUploadSettingsStore.userUploadsWithoutCode) return;

        if (source === null) {
            this._selectedSourceForUpload = null;
            window.history.pushState({}, "", this.baseUrl);
        } else {
            const foundSource = this.sourcesAvailableForUpload.find((s) => s.id === source.id);
            if (foundSource) {
                window.history.pushState({}, "", this.baseUrl + "/" + foundSource.name);
                this._selectedSourceForUpload = foundSource;
            }
        }
    }

    @action
    setUploadCode(uploadCode: string) {
        this.uploadCode = uploadCode;
    }

    @computed
    public get uploadCodeLengthOk(): boolean {
        if (this.currentUploadSettingsStore.userUploadsWithoutCode) return true;
        if (this.uploadCode == null) return false;
        return this.uploadCode.length === this.uploadCodeLength;
    }

    @action
    private setUploadCodeAndSourceValid(valid: boolean | null): void {
        this._uploadCodeAndSourceValid = valid;
    }

    @computed
    public get ableToValidateCodeAndSource(): boolean {
        return this.selectedSourceForUpload != null && this.uploadCodeLengthOk;
    }

    @action
    private setValidating(validating: boolean) {
        this.validating = validating;
    }

    @action
    private setUploadCodeError(error: boolean) {
        this.uploadCodeError = error;
    }

    @action
    private setExpiredCodeError(status: boolean) {
        this.expiredUploadCode = status;
    }

    @action
    private setSourceSelectionError(error: boolean) {
        this.sourceSelectionError = error;
    }

    public async validateCodeAndSource(): Promise<void> {
        this.setSourceSelectionError(!this.selectedSourceForUpload);
        this.setUploadCodeError(!this.uploadCodeLengthOk);

        if (!this.ableToValidateCodeAndSource) return;

        this.setValidating(true);
        const validationResult = await this.codeAndSourceValidationFetcher.validate(this.selectedSourceForUpload!.id, this.uploadCode!);

        this.setExpiredCodeError(validationResult.expired);
        this.setUploadCodeAndSourceValid(validationResult.validated);

        this.setValidating(false);
    }
    
    private readonly sortFunction = (a: UploadSource, b: UploadSource) => {
        return a.description && b.description
            ? a.description.toLowerCase().localeCompare(b.description.toLowerCase())
            : 0;
    };
}
